import { type ChainflipChain, type AnyChainflipChain } from '@chainflip/utils/chainflip';
import { type Chain as WagmiChain } from 'wagmi/chains';
import mainnetChains from '@/shared/assets/chains/mainnet';
import testnetChains from '@/shared/assets/chains/testnet';
import { isTestnet } from '@/shared/featureFlags';

export type ChainId =
  | `evm-${number}`
  | `cosmos-${string}`
  | 'hub'
  | 'hub-testnet'
  | 'btc'
  | 'btc-testnet'
  | 'sol'
  | 'sol-devnet'
  | 'sol-backspin';

export type AnyChainId = ChainId | 'dot' | 'dot-testnet';

type ChainDataBase = {
  name: string;
  addressPlaceholder: string;
  wagmiChain: WagmiChain | undefined;
};

export interface AnyChainData extends ChainDataBase {
  id: AnyChainId;
}

export interface ChainData extends AnyChainData {
  id: ChainId;
}

export interface AnyChainflipChainData extends AnyChainData {
  id: AnyChainId;
  chainflipId: AnyChainflipChain;
}

// Avoid name collision with toolkit and wagmi
export interface ChainflipChainData extends ChainData {
  id: ChainId;
  chainflipId: ChainflipChain;
}

const allChains = [...mainnetChains, ...testnetChains];
export const chains: (ChainData | ChainflipChainData)[] = isTestnet ? testnetChains : mainnetChains;

export type ConfigChainId = (typeof allChains)[number]['id'];
export type SupportedChainId = ConfigChainId | ChainId;

export const chainById = (id: AnyChainId): ChainData | ChainflipChainData | undefined =>
  chains.find((c) => c.id === id);

// Third-party integration tokens have no chainflipId, so they fall back to the name.
export const chainflipIdOrChainName = (chain: ChainData | ChainflipChainData | AnyChainData) =>
  'chainflipId' in chain ? chain.chainflipId : chain.name;

export const isEvmChain = (chainId?: AnyChainId): chainId is `evm-${number}` =>
  chainId !== undefined && /evm-\d+/.test(chainId);

export const parseEvmChainId = (chainId?: AnyChainId): number | undefined => {
  if (!isEvmChain(chainId)) return undefined;
  return Number(chainId.split('-')[1]);
};

export const isSolanaChain = (
  chainId?: AnyChainId,
): chainId is 'sol' | 'sol-devnet' | 'sol-backspin' =>
  chainId === 'sol' || chainId === 'sol-devnet' || chainId === 'sol-backspin';

// Mainnet: 728126428, Nile testnet: 3448148188, Backspin: 4271970548
export const isTronChain = (
  chainId?: AnyChainId,
): chainId is 'evm-728126428' | 'evm-3448148188' | 'evm-4271970548' =>
  chainId === 'evm-728126428' || chainId === 'evm-3448148188' || chainId === 'evm-4271970548';

export const isLegacyChain = (chainId: AnyChainId): chainId is 'dot' | 'dot-testnet' =>
  chainId === 'dot' || chainId === 'dot-testnet';

export * from '@/shared/assets/chains/mainnet';
export * from '@/shared/assets/chains/testnet';
